import { useEffect, useState } from 'react';
import {
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';

// third-party
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import ScrollSpy from 'react-scrollspy-navigation';
// assets

import {
  createUpdateFields,
  getFieldDetail,
} from '../../../actions/promotion/fields';

import OptionValues from './OptionValues';
import { If, Then } from 'react-if';
import { getAllConfigs } from '../../../actions/promotion/generals';
import ErrorMessagesInLang from './ErrorMessagesInLang';
import { setGlobalLanguages } from '../../../slices/dashboard/reducer';
import { useDispatch } from 'react-redux';

function getStyles(name, country, theme) {
  return {
    fontWeight:
      country?.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

// ==============================|| CUSTOMER - ADD / EDIT ||============================== //

const fieldGroups = {
  basket: { label: 'Basket', fieldTypes: [] },
  user: { label: 'User', fieldTypes: [] },
  item: { label: 'Item', fieldTypes: [] },
  derived: { label: 'Derived', fieldTypes: [] },
  reward: { label: 'Reward', fieldTypes: [] },
  sys: { label: 'System', fieldTypes: [] },
};
const fieldDataTypes = {
  string: { label: 'String', fieldTypes: [] },
  json: { label: 'JSON', fieldTypes: ['jsonValue'] },
  integer: { label: 'Integer', fieldTypes: ['number'] },
  float: { label: 'Float', fieldTypes: ['number'] },
  double: { label: 'Double', fieldTypes: ['number'] },
  boolean: { label: 'Boolean', fieldTypes: ['radio'] },
  datetime: { label: 'Datetime', fieldTypes: ['datetime', 'date'] },
};

const fieldTypes = {
  text: { label: 'Text Field' },
  number: { label: 'Number Field' },
  textarea: { label: 'Textarea' },
  multiselect: { label: 'Multi Select' },
  select: { label: 'Dropdown' },
  // image: { label: 'Image Upload' },
  datetime: { label: 'Date & Time' },
  date: { label: 'Date' },
  radio: { label: 'Single Select (Radio)' },
  checkbox: { label: 'Multi Select (Checkbox)' },
  switch: { label: 'Switch' },
  treeData: { label: 'Tree Picker' },
  jsonValue: { label: 'JSON values' },

  // "text" | "select" | "checkbox" | "radio" | "textarea" | "switch" | "multiselect" | null
};
const fieldGroupArray = Object.keys(fieldGroups).map((key) => ({
  label: fieldGroups[key].label,
  value: key,
}));
const fieldTypesArray = Object.keys(fieldTypes).map((key) => ({
  label: fieldTypes[key].label,
  value: key,
}));
const fieldDataTypesArray = Object.keys(fieldDataTypes).map((key) => ({
  label: fieldDataTypes[key].label,
  value: key,
  fieldTypes: fieldDataTypes[key].fieldTypes,
}));

const AddFields = ({
  getFields = () => {},
  fieldDetailFromProp = {},
  onCancel = () => {},
  show = false,
}) => {
  const fieldId = fieldDetailFromProp?._id || 'new';
  const isCreating = false;

  const [fieldDetail, setFieldDetail] = useState({ ...fieldDetailFromProp });

  const fetchFieldDetail = async (fieldId) => {
    const result = await getFieldDetail({ fieldId });
    if (result?.getFieldDetail?.optionValues) {
      result.getFieldDetail.optionValues =
        result?.getFieldDetail?.optionValues.map((option) => {
          delete option?.__typename;
          return option;
        });
    }
    if (result?.getFieldDetail?.errorMessagesLang) {
      result.getFieldDetail.errorMessagesLang =
        result?.getFieldDetail?.errorMessagesLang.map((option) => {
          delete option?.__typename;
          return option;
        });
    }

    setFieldDetail(result?.getFieldDetail);
  };
  const [languages, setLanguages] = useState([]);
  const dispatch = useDispatch();
  const getConfigs = async () => {
    try {
      const { data } = await getAllConfigs(
        {
          configName: 'languages',
          configEnv: process.env.NODE_ENV,
          pageOffset: 0,
          pageSize: 60,
        },
        dispatch
      );
      console.log('setLanguages', data);

      const lang = data.map((item) => {
        return {
          value: item?.values?.find((val) => val?.fieldCode === 'code')
            ?.fieldValue,
          label: item?.values?.find((val) => val?.fieldCode === 'name')
            ?.fieldValue,
        };
      });
    } catch (error) {
      console.log(error);
    }
  };
  const isNew = fieldId === 'new';
  useEffect(() => {
    if (fieldId && fieldId !== 'new') {
      fetchFieldDetail(fieldId);
    }
  }, [fieldDetailFromProp, fieldId]);
  useEffect(() => {
    getConfigs();
  }, []);

  const {
    fieldGroup = '',
    fieldName = '',
    fieldCode = '',
    dataType = 'string',
    fieldType = '',
    defaultValue = '',
    treeData = '',
    fieldUsedIn = [],
    isMultiple = false,
    canCompareWithOtherFieldValue = false,
    enableSumQty = false,
    enableSumTotal = false,
    enableContains = false,
    optionValues = [],
    valueSource = [],
    errorMessages = '',
    errorMessagesLang = [],
    placeholder = '',
  } = fieldDetail || {};
  const StoreSchema = Yup.object().shape({
    fieldGroup: Yup.string()
      .required('Field Group is required')
      .oneOf(Object.keys(fieldGroups)),
    fieldName: Yup.string()

      .max(100, 'Field Name must be at most 100 characters')
      .min(2, 'Field Name must be at least 5 characters')
      .required('Field Name is required')
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        'Only letters, numbers, and spaces are allowed'
      ),
    fieldCode: Yup.string()
      .max(50)
      .min(2)
      .matches(
        /^[a-zA-Z0-9_\.]*$/,
        'Only letters, numbers, and underscores are allowed'
      ),
    dataType: Yup.string().oneOf(Object.keys(fieldDataTypes)),
    defaultValue: Yup.mixed().test({
      name: 'value-test',

      test: (value, t) => {
        if (!value) return true;
        // const selectedDatType = getFieldValues('dataType');
        switch (t.parent.dataType) {
          default:
          case 'string':
            return (
              typeof value === 'string' ||
              t.createError({
                message: 'Value must be a String',
                path: 'defaultValue',
              })
            );
          case 'integer':
            return (
              Number.isInteger(Number(value)) ||
              t.createError({
                message: 'Value must be a Integer',
                path: 'defaultValue',
              })
            );
          case 'float':
            return (
              typeof Number(value) === 'number' ||
              t.createError({
                message: 'Value must be a Float',
                path: 'defaultValue',
              })
            );
          case 'double':
            return (
              typeof Number(value) === 'number' ||
              t.createError({
                message: 'Value must be a number',
                path: 'defaultValue',
              })
            );
          case 'boolean':
            return (
              typeof value === 'boolean' ||
              t.createError({
                message: 'Value must be a boolean',
                path: 'defaultValue',
              })
            );
          case 'datetime':
            return (
              value instanceof Date ||
              t.createError({
                message: 'Value must be a DateTime',
                path: 'defaultValue',
              })
            );
        }
      },
    }),
    // .when('dataType', {
    //   is: 'boolean',
    //   then: ()=>Yup.boolean(),
    // }),
    // dateFormat: Yup.string().when('dataType', {
    //   is: 'datetime',
    //   then: ()=>Yup.string().required('Date time Format is required'),
    // }),
    fieldType: Yup.string().oneOf(Object.keys(fieldTypes)),
    // jsonValue: Yup.string().when('fieldType', {
    //   is: 'json',
    //   then: ()=>Yup.string().required('JSON value is required'),
    // }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fieldGroup,
      fieldName,
      fieldCode,
      dataType,
      fieldType,
      defaultValue,
      treeData,
      fieldUsedIn,
      isMultiple,
      canCompareWithOtherFieldValue,
      enableSumQty,
      enableSumTotal,
      enableContains,
      optionValues,
      valueSource,
      errorMessages,
      errorMessagesLang,
      placeholder,
    },
    // validationSchema: StoreSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        let success;
        let message;
        let error;
        if (fieldId) {
          const inputData = {
            ...values,
          };
          // Call create user API

          const {
            id,
            success: createStoreSuccess,
            message: createMessage,
            error: gqError,
          } = await createUpdateFields(values, fieldId);
          // message = createMessage || error.message;
          success = createStoreSuccess;
          if (createStoreSuccess) {
            getFields();
            //  setIsOpenModal(false);
            onCancel();
          }

          error = gqError;
        }
        if (success) {
          alertSuccess(
            isCreating
              ? 'Field created successfully.'
              : 'Field updated successfully.'
          );
          getFields();
        } else {
          alertError('Failed!. Please try again later..');
        }

        setSubmitting(false);
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    getFieldValues,
    setFieldValue,
  } = formik;

  // console.error(errors);
  const fieldTypeState = getFieldProps('fieldType');
  const dataTypeState = getFieldProps('dataType');
  const isMultipleProp = getFieldProps('isMultiple');
  const canCompareWithOtherFieldValueProp = getFieldProps(
    'canCompareWithOtherFieldValue'
  );
  const enableSumQtyProp = getFieldProps('enableSumQty');
  const enableSumTotalProp = getFieldProps('enableSumTotal');
  const enableContainsProp = getFieldProps('enableContains');

  const [activeSection, setActiveSection] = useState('target-1');
  const [state, setState] = useState({
    dropdownOpen: {},
    direction: 'down',
    showModal: false,
    selectedAction: null,
    showFieldModal: false,
    formField: false,
    showCreateModal: false,
  });
  const allFieldProps = formik.getFieldProps();

  const isLargeModal =
    allFieldProps.value.fieldGroup && allFieldProps.value.fieldType
      ? true
      : false;
  return (
    <FormikProvider value={formik}>
      {/* Field Modal */}

      <Modal
        className={isLargeModal ? 'modal-fullscreen' : ''}
        size={'lg'}
        isOpen={show}
        toggle={() => {
          onCancel();
          // setIsOpenModal(!isOpenModal);
        }}
        backdrop="static"
      >
        <ModalHeader
          className="modal-title pb-3 bg-light"
          toggle={() => {
            onCancel();
            // setIsOpenModal(!isOpenModal);
          }}
        >
          <h6 className="modal-title">
            {fieldDetail?.fieldName} -{' '}
            <span className="text-muted">
              {isNew ? 'Create new field' : 'Edit Field'}
            </span>
          </h6>
        </ModalHeader>
        <ModalBody>
          <If condition={!allFieldProps?.value?.fieldGroup}>
            <Then>
              <div id="selectField">
                <Row>
                  {fieldGroupArray?.map((fieldGroupItem) => (
                    <Col md={4} className="mb-3">
                      <div
                        className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                        onClick={() => {
                          formik.setFieldValue(
                            'fieldGroup',
                            fieldGroupItem.value
                          );
                          // setState({
                          //   ...state,
                          //   formField: fieldGroupItem.value,
                          // });
                        }}
                      >
                        <i className="ri-shopping-basket-2-line fs-24 lh-1 mb-2"></i>
                        <h6 className="m-0">{fieldGroupItem.label}</h6>
                        <p className="m-0 text-muted fs-13">
                          Text formatting with references and media
                        </p>
                      </div>
                    </Col>
                  ))}
                  {/* <Col md={4} className="mb-3">
                    <div
                      className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                      onClick={() => {
                        setState({ ...state, formField: true });
                      }}
                    >
                      <i className="ri-user-6-line fs-24 lh-1 mb-2"></i>
                      <h6 className="m-0">User</h6>
                      <p className="m-0 text-muted fs-13">
                        Text formatting with references and media
                      </p>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <div
                      className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                      onClick={() => {
                        setState({ ...state, formField: true });
                      }}
                    >
                      <i className="ri-file-list-2-line fs-24 lh-1 mb-2"></i>
                      <h6 className="m-0">Item</h6>
                      <p className="m-0 text-muted fs-13">
                        Text formatting with references and media
                      </p>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <div
                      className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                      onClick={() => {
                        setState({ ...state, formField: true });
                      }}
                    >
                      <i className="ri-sticky-note-2-line fs-24 lh-1 mb-2"></i>
                      <h6 className="m-0">Derived</h6>
                      <p className="m-0 text-muted fs-13">
                        Text formatting with references and media
                      </p>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <div
                      className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                      onClick={() => {
                        setState({ ...state, formField: true });
                      }}
                    >
                      <i className="ri-medal-line fs-24 lh-1 mb-2"></i>
                      <h6 className="m-0">Reward</h6>
                      <p className="m-0 text-muted fs-13">
                        Text formatting with references and media
                      </p>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <div
                      className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                      onClick={() => {
                        setState({ ...state, formField: true });
                      }}
                    >
                      <i className="ri-apps-2-line fs-24 lh-1 mb-2"></i>
                      <h6 className="m-0">System</h6>
                      <p className="m-0 text-muted fs-13">
                        Text formatting with references and media
                      </p>
                    </div>
                  </Col> */}
                </Row>
              </div>
            </Then>
          </If>
          <If
            condition={
              allFieldProps?.value?.fieldGroup &&
              !allFieldProps?.value?.fieldType
            }
          >
            <Then>
              <div id="formField">
                <ModalBody>
                  <form className="fs-14">
                    <Row>
                      {fieldTypesArray?.map((fieldTypeItem) => {
                        if (
                          fieldDataTypes?.[
                            dataTypeState?.value
                          ]?.fieldTypes?.includes(fieldTypeItem.value) ||
                          !fieldDataTypes?.[dataTypeState?.value]?.fieldTypes
                            ?.length
                        ) {
                          return (
                            <Col md={4} className="mb-3">
                              <div
                                className="cursor-pointer p-3 border rounded d-flex flex-column gap-2 h-100"
                                onClick={() => {
                                  formik.setFieldValue(
                                    'fieldType',
                                    fieldTypeItem.value
                                  );
                                  // setState({ ...state, formField: false });
                                }}
                              >
                                <i className="ri-apps-2-line fs-24 lh-1 mb-2"></i>
                                <h6 className="m-0">{fieldTypeItem.label}</h6>
                                <p className="m-0 text-muted fs-13">
                                  Text formatting with references and media
                                </p>
                              </div>
                            </Col>
                          );
                        }
                      })}
                    </Row>
                    <Row>
                      <Col md={6} className="mb-3">
                        <label className="form-label">Field Name</label>
                        <input type="text" className="form-control" />
                      </Col>
                    </Row>
                  </form>
                </ModalBody>
                <div className="modal-footer pt-3 border-top">
                  <Button
                    outline
                    color="dark"
                    onClick={() => setState({ ...state, formField: false })}
                  >
                    Change field type
                  </Button>
                  <Button
                    color="success"
                    onClick={() => {
                      setState({
                        ...state,
                        showCreateModal: true,
                        showFieldModal: false,
                      });
                    }}
                  >
                    Add and configure
                  </Button>
                </div>
              </div>
            </Then>
          </If>
          <If
            condition={
              allFieldProps?.value?.fieldGroup &&
              allFieldProps?.value?.fieldType
            }
          >
            <Then>
              <>
                <div className="d-flex align-items-start">
                  <Col md="2" className="position-sticky" style={{ top: 0 }}>
                    <ScrollSpy activeClass="bg-light ">
                      <nav className="p-3">
                        <ul className="p-0 list-unstyled">
                          {[
                            { id: 'target-1', label: 'Name and field ID' },
                            { id: 'target-2', label: 'Settings' },
                            { id: 'target-3', label: 'Validation' },
                            { id: 'target-4', label: 'Default value' },
                            { id: 'target-5', label: 'Appearance' },
                          ].map((item) => (
                            <li key={item.id}>
                              <a
                                href={`#${item.id}`}
                                className={`fs-14 rounded-3 overflow-hidden d-block p-2 ${
                                  activeSection === item.id ? 'bg-light' : ''
                                }`}
                                onClick={() => setActiveSection(item.id)}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </ScrollSpy>
                  </Col>
                  <Col>
                    <Form autoComplete="off" onSubmit={handleSubmit}>
                      <section
                        id="target-1"
                        className="scrollSection d-flex flex-column align-items-start justify-content-start gap-3 w-100 p-4"
                      >
                        <h5>Name and field ID</h5>

                        <Row>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="field-fieldGroup">Field Group</Label>
                              <Input
                                type="select"
                                id="fieldGroup"
                                {...getFieldProps('fieldGroup')}
                                invalid={
                                  touched.fieldGroup && !!errors.fieldGroup
                                }
                              >
                                {fieldGroupArray?.map((fieldGroupItem) => (
                                  <option
                                    key={fieldGroupItem.value}
                                    value={fieldGroupItem.value}
                                  >
                                    {fieldGroupItem.label}
                                  </option>
                                ))}
                              </Input>
                              {touched.fieldGroup && errors.fieldGroup && (
                                <FormFeedback>{errors.fieldGroup}</FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="field-name">Field Name</Label>
                              <Input
                                type="text"
                                id="field-name"
                                placeholder="Enter Field Name"
                                {...getFieldProps('fieldName')}
                                invalid={
                                  touched.fieldName && !!errors.fieldName
                                }
                              />
                              {touched.fieldName && errors.fieldName && (
                                <FormFeedback>{errors.fieldName}</FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="field-code">Field Code</Label>
                              <Input
                                type="text"
                                id="field-code"
                                placeholder="Enter Field Code"
                                {...getFieldProps('fieldCode')}
                                invalid={
                                  touched.fieldCode && !!errors.fieldCode
                                }
                              />
                              {touched.fieldCode && errors.fieldCode && (
                                <FormFeedback>{errors.fieldCode}</FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="field-dataType">Data Type</Label>
                              <Input
                                type="select"
                                id="dataType"
                                {...getFieldProps('dataType')}
                                invalid={touched.dataType && !!errors.dataType}
                              >
                                {fieldDataTypesArray?.map((dataTypeItem) => (
                                  <option
                                    key={dataTypeItem.value}
                                    value={dataTypeItem.value}
                                  >
                                    {dataTypeItem.label}
                                  </option>
                                ))}
                              </Input>
                              {touched.dataType && errors.dataType && (
                                <FormFeedback>{errors.dataType}</FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="field-fieldType">
                                Field Display Type
                              </Label>
                              <Input
                                type="select"
                                id="fieldType"
                                {...getFieldProps('fieldType')}
                                invalid={
                                  touched.fieldType && !!errors.fieldType
                                }
                              >
                                {fieldTypesArray?.map((fieldTypeItem) => {
                                  if (
                                    fieldDataTypes?.[
                                      dataTypeState?.value
                                    ]?.fieldTypes?.includes(
                                      fieldTypeItem.value
                                    ) ||
                                    !fieldDataTypes?.[dataTypeState?.value]
                                      ?.fieldTypes?.length
                                  ) {
                                    return (
                                      <option
                                        key={fieldTypeItem.value}
                                        value={fieldTypeItem.value}
                                      >
                                        {fieldTypeItem.label}
                                      </option>
                                    );
                                  }
                                  return null;
                                })}
                              </Input>
                              {touched.fieldType && errors.fieldType && (
                                <FormFeedback>{errors.fieldType}</FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="default-value">Default Value</Label>
                              <Input
                                type="text"
                                id="default-value"
                                placeholder="Enter Default Value"
                                {...getFieldProps('defaultValue')}
                                invalid={
                                  touched.defaultValue && !!errors.defaultValue
                                }
                              />
                              {touched.defaultValue && errors.defaultValue && (
                                <FormFeedback>
                                  {errors.defaultValue}
                                </FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <FormGroup>
                              <Label for="placeholder-value">Placeholder</Label>
                              <Input
                                type="text"
                                id="placeholder-value"
                                placeholder="Enter Placeholder"
                                {...getFieldProps('placeholder')}
                                invalid={
                                  touched.placeholder && !!errors.placeholder
                                }
                              />
                              {touched.placeholder && errors.placeholder && (
                                <FormFeedback>
                                  {errors.placeholder}
                                </FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <If condition={fieldTypeState.value === 'treeData'}>
                            <Col md={12} className="mb-3">
                              <FormGroup>
                                <Label for="tree-value">Tree Data</Label>
                                <Input
                                  type="textarea"
                                  id="tree-data"
                                  placeholder="Enter Tree Data(JSON)"
                                  {...getFieldProps('treeData')}
                                  invalid={
                                    touched.treeData && !!errors.treeData
                                  }
                                />
                                {touched.treeData && errors.treeData && (
                                  <FormFeedback>{errors.treeData}</FormFeedback>
                                )}
                              </FormGroup>
                            </Col>
                          </If>
                          <Col md={12} className="mb-3">
                            <FormGroup>
                              <Label for="optionValues-value">
                                Option Values
                              </Label>
                              <OptionValues
                                {...getFieldProps('optionValues')}
                                onChange={(values) => {
                                  setFieldValue('optionValues', values);
                                }}
                              />
                            </FormGroup>
                          </Col>

                          <Col
                            md={12}
                            className="d-flex align-items-center justify-content-start flex-wrap gap-2 mb-3"
                          >
                            <FormGroup check className="border rounded-5 ">
                              <Label check className="px-3 py-2">
                                <Input
                                  type="checkbox"
                                  id="can-canCompareWithOtherFieldValue"
                                  {...canCompareWithOtherFieldValueProp}
                                  checked={
                                    canCompareWithOtherFieldValueProp.value
                                  }
                                  // checked={values.isMultiple}
                                  invalid={
                                    touched.canCompareWithOtherFieldValue &&
                                    !!errors.canCompareWithOtherFieldValue
                                  }
                                />
                                Can Compare With Other Fields
                              </Label>
                              {touched.canCompareWithOtherFieldValue &&
                                errors.canCompareWithOtherFieldValue && (
                                  <FormFeedback>
                                    {errors.canCompareWithOtherFieldValue}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                            <FormGroup check className="border rounded-5 ">
                              <Label check className="px-3 py-2">
                                <Input
                                  type="checkbox"
                                  id="is-multiple"
                                  {...isMultipleProp}
                                  checked={isMultipleProp.value}
                                  // checked={values.isMultiple}
                                  invalid={
                                    touched.isMultiple && !!errors.isMultiple
                                  }
                                />
                                Is Multiple
                              </Label>
                              {touched.isMultiple && errors.isMultiple && (
                                <FormFeedback>{errors.isMultiple}</FormFeedback>
                              )}
                            </FormGroup>
                            <FormGroup check className="border rounded-5 ">
                              <Label check className="px-3 py-2">
                                <Input
                                  type="checkbox"
                                  id="enableSumQty"
                                  {...getFieldProps('enableSumQty')}
                                  checked={enableSumQtyProp?.value}
                                  invalid={
                                    touched.enableSumQty &&
                                    !!errors.enableSumQty
                                  }
                                />
                                Enable Count Sum Of Quantities
                              </Label>
                              {touched.enableSumQty && errors.enableSumQty && (
                                <FormFeedback>
                                  {errors.enableSumQty}
                                </FormFeedback>
                              )}
                            </FormGroup>
                            <FormGroup check className="border rounded-5 ">
                              <Label check className="px-3 py-2">
                                <Input
                                  type="checkbox"
                                  id="enableSumTotal"
                                  {...getFieldProps('enableSumTotal')}
                                  checked={enableSumTotalProp?.value}
                                  invalid={
                                    touched.enableSumTotal &&
                                    !!errors.enableSumTotal
                                  }
                                />
                                Enable Sub Total
                              </Label>
                              {touched.enableSumTotal &&
                                errors.enableSumTotal && (
                                  <FormFeedback>
                                    {errors.enableSumTotal}
                                  </FormFeedback>
                                )}
                            </FormGroup>

                            <FormGroup check className="border rounded-5 ">
                              <Label check className="px-3 py-2">
                                <Input
                                  type="checkbox"
                                  id="enableContains"
                                  {...getFieldProps('enableContains')}
                                  checked={enableContainsProp?.value}
                                  invalid={
                                    touched.enableContains &&
                                    !!errors.enableContains
                                  }
                                />
                                Enable Contains Check
                              </Label>
                              {touched.enableContains &&
                                errors.enableContains && (
                                  <FormFeedback>
                                    {errors.enableContains}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={6} className="mb-3">
                            <label className="form-label">
                              Default Error Message
                            </label>
                            <input type="text" className="form-control" />
                          </Col>
                        </Row>
                      </section>
                      <section
                        id="target-2"
                        className="scrollSection d-flex flex-column align-items-start justify-content-start gap-3 w-100 p-4"
                      >
                        <h5>Settings</h5>
                        dvsdv
                      </section>
                      <section
                        id="target-3"
                        className="scrollSection d-flex flex-column align-items-start justify-content-start gap-3 w-100 p-4"
                      >
                        <h5>Validation</h5>
                        dvsdv
                      </section>
                      <section
                        id="target-4"
                        className="scrollSection d-flex flex-column align-items-start justify-content-start gap-3 w-100 p-4"
                      >
                        <h5>Default value</h5>
                        dvsdv
                      </section>
                      <section
                        id="target-5"
                        className="scrollSection d-flex flex-column align-items-start justify-content-start gap-3 w-100 p-4"
                      >
                        <h5>Appearance</h5>
                        dvsdv
                      </section>
                      <Row className="mt-4">
                        <Col lg={12}>
                          <div className="hstack gap-2 justify-content-end">
                            <Button
                              color="light"
                              onClick={onCancel}
                              className="w-auto"
                            >
                              Close
                            </Button>
                            <Button
                              color="primary w-auto"
                              disabled={isSubmitting}
                              type="submit"
                            >
                              {fieldDetail?.fieldName
                                ? 'Save Draft'
                                : 'Save & Next'}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </Col>
                </div>

                {/* <Row>
              <Col className="text-end">
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {fieldDetail?.fieldName ? "Save Draft" : "Save & Next"}
                </Button>
              </Col>
            </Row> */}
              </>
            </Then>
          </If>
        </ModalBody>
      </Modal>
    </FormikProvider>
  );
};

export default AddFields;
